'use client';

import { ArrowLeft } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { formatLocaleNumber } from '@kit/shared/utils';

export function AskAiTermsOfService() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const year = formatLocaleNumber(new Date().getFullYear(), i18n.language, {
    useGrouping: false,
  });

  // Load Google Fonts dynamically
  useEffect(() => {
    if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(preconnect1);

      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect2);
    }

    if (!document.querySelector('link[href*="family=Outfit"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href =
        'https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
      document.head.appendChild(fontLink);
    }
  }, []);

  return (
    <>
      <style jsx global>{`
        .legal-page {
          font-family: 'Outfit', system-ui, sans-serif;
        }
        .legal-page .font-mono {
          font-family: 'JetBrains Mono', monospace;
        }
      `}</style>
      <div className="legal-page min-h-screen bg-[#0a0a0f] text-white">
        {/* Subtle gradient orbs */}
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-[#7B52F0]/10 blur-[120px]" />
          <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#6236C9]/10 blur-[120px]" />
        </div>

        {/* Header */}
        <header className="relative z-10 border-b border-gray-800/50 px-8 py-6">
          <nav className="mx-auto flex max-w-4xl items-center justify-between">
            <Link href="/" className="group flex items-center gap-3">
              <Image
                src="/images/askai/Logo.png"
                alt={t('common:brand.askAiLogoAlt')}
                width={145}
                height={32}
                className="h-8 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
            <Link
              href="/"
              className="flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common:back')}
            </Link>
          </nav>
        </header>

        {/* Content */}
        {isArabic ? <ArabicTermsBody /> : <EnglishTermsBody />}

        {/* Footer */}
        <footer className="relative z-10 border-t border-gray-800/50">
          <div className="mx-auto max-w-4xl px-8 py-8">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <p className="font-mono text-sm text-gray-600">
                &copy; {year} ASK AI. {t('marketing:legal.allRightsReserved')}
              </p>
              <div className="flex items-center gap-6">
                <Link
                  href="/privacy-policy"
                  className="text-sm text-gray-600 transition-colors hover:text-gray-400"
                >
                  {t('marketing:legal.privacy')}
                </Link>
                <span className="text-sm text-gray-400">
                  {t('marketing:legal.terms')}
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

function EnglishTermsBody() {
  return (
    <main className="relative z-10 px-8 py-16">
      <div className="mx-auto max-w-4xl">
        <h1 className="mb-4 text-4xl font-light tracking-tight">Terms of Use</h1>
        <p className="mb-12 font-mono text-sm text-gray-500">
          Last Updated: 16 April 2025
        </p>

        <div className="space-y-6 leading-relaxed text-gray-400">
          <p>
            These Terms of Use (&quot;Terms&quot;) constitute a legally binding
            agreement between you and Ask AI (&quot;We&quot;, &quot;Us&quot;,
            &quot;Our&quot;). By accessing or using the Ask AI Platform
            (hereafter referred to as the &quot;Platform&quot;), you agree to be
            bound by these Terms. If you do not agree with any part of these
            Terms, you must refrain from using or accessing the Platform and
            related Services.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            1. Introduction
          </h2>
          <p>
            Welcome to Ask AI, a software platform for businesses and
            individuals, enabling AI-powered customer interactions, booking
            management, and business automation. Our Platform allows you to
            create, manage, and analyse intelligent solutions efficiently.
            Please read these Terms carefully as they govern your use of our
            Website, Services, and Platform. Should you have any questions or
            concerns about these Terms or our Services, please contact us at
            privacy@askai.sa.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            2. Platform Access
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            2.1 Eligibility
          </h3>
          <p>
            We do not knowingly collect or solicit personal information from
            children under 13. If you are under 13, please do not attempt to
            register for our Services or provide us with any personal
            information.
          </p>
          <p>
            If we become aware that we have collected personal information from
            a child under 13, we will delete such information promptly.
          </p>
          <p>
            If you believe that we might have collected personal information
            from a child under 13, please notify us at privacy@askai.sa.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            2.2 Registration and Account Security
          </h3>
          <p>
            To use certain features of the Platform, you must register and
            create an Account, including a Username and Password.
          </p>
          <p>
            Your Account is for your personal use only and should not be shared.
            Any breach of this policy may result in the termination of the
            affected Accounts or all Accounts associated with you.
          </p>
          <p>
            If you suspect that your Account credentials have been compromised,
            please contact us immediately. Note that we will never ask you to
            confirm your Username, Password, or other personal details via
            unsolicited communications.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            3. Contact Information
          </h2>
          <p className="font-semibold text-white">Ask AI</p>
          <p>Al Wourud, Riyadh, Saudi Arabia</p>
          <p>Email: privacy@askai.sa</p>
          <p>Phone: +966 11 205 3030</p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            4. Usage of the Platform
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            4.1 Services
          </h3>
          <p>
            We provide you with a Platform interface and a suite of Services
            (&quot;Services&quot;) designed to enable you to subscribe to and
            utilise our Platform for AI-powered customer interactions, booking
            management, chatbot creation, and business automation. These
            Services are offered in their existing state and may assist in
            enhancing your business processes.
          </p>
          <p>
            Upon registration, you will be granted access to the Platform and
            its associated functionalities in accordance with the Terms outlined
            in this document.
          </p>
          <p>
            Your continued use of the Services indicates acceptance of these
            Terms, which include all clauses contained in this document as well
            as our Privacy Policy.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            5. Limitations of Service
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.1 Responsibility for Data
          </h3>
          <p>
            You acknowledge and agree that you are solely responsible for
            evaluating the integrity, quality, accuracy, or reliability of any
            data provided to us. Any decisions based on this data and any
            resulting consequences are solely your responsibility.
          </p>
          <p>
            You acknowledge that we do not assume any liability for any data
            handled or generated by you on the Platform.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.2 Service Revisions
          </h3>
          <p>
            We are continually working to improve our Platform and may introduce
            modifications or revisions to its functionality, content, features,
            and modules, or discontinue certain aspects of the Services, at our
            discretion and without notice.
          </p>
          <p>
            We reserve the right to remove any Content from the Services at any
            time, for any reason, including but not limited to if there are
            allegations that the Content violates these Terms. Such removal will
            be at our sole discretion and without notice. For any service
            disruptions or interruptions, please contact us at privacy@askai.sa
            for support and clarification.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.3 Temporary Suspension of Services
          </h3>
          <p>
            We may temporarily limit or suspend the Services at our discretion,
            including for maintenance or upgrades to the Platform.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.4 Unacceptable Use of Services and User Conduct
          </h3>
          <p>
            You represent, warrant, and agree that you will not create or
            contribute any Content or User Submissions, or otherwise use the
            Services in a manner that:
          </p>
          <ul className="my-4 list-disc space-y-2 ps-6">
            <li>
              Infringes or violates the intellectual property rights or any
              other rights of any third party (including Ask AI)
            </li>
            <li>
              Violates any laws or regulations, including applicable export
              control laws
            </li>
            <li>
              Is harmful, fraudulent, deceptive, threatening, harassing,
              defamatory, obscene, or otherwise objectionable
            </li>
            <li>
              Jeopardises the security of your Ask AI account or others&apos;
              accounts
            </li>
            <li>
              Attempts to obtain passwords, accounts, or other security
              information from other users
            </li>
            <li>
              Violates the security of any computer network, or attempts to
              crack passwords or security encryption codes
            </li>
            <li>
              Runs any automated systems or processes that interfere with the
              proper functioning of the Services
            </li>
            <li>
              Crawls, scrapes, or spiders any page, data, or portion of the
              Services or Content using manual or automated means
            </li>
            <li>
              Decompiles, reverse engineers, or attempts to obtain the source
              code or underlying ideas of the Services
            </li>
          </ul>
          <p>
            A violation of any of the above is grounds for the termination of
            your right to use or access the Services, with or without notice.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            6. User Declarations and Assurances
          </h2>
          <p>
            By accessing and using the Platform, you represent and warrant the
            following:
          </p>
          <ul className="my-4 list-disc space-y-2 ps-6">
            <li>
              <strong className="text-white">Accuracy of Information:</strong>{' '}
              You affirm that all information provided during the registration
              process is truthful, accurate, current, and complete to the best
              of your knowledge.
            </li>
            <li>
              <strong className="text-white">Legal Capacity:</strong> You
              represent that you have the legal capacity and authority to enter
              into these Terms and Conditions and to abide by all obligations
              and responsibilities set forth herein.
            </li>
            <li>
              <strong className="text-white">Age Requirements:</strong> You
              confirm that you are at least 13 years old. If you are under 18
              years of age, you represent that you have obtained parental or
              guardian consent to use the Platform.
            </li>
            <li>
              <strong className="text-white">No Automated Access:</strong> You
              agree not to access the Platform using any automated systems,
              including but not limited to bots, scripts, or other automated
              means that bypass the normal user interface.
            </li>
            <li>
              <strong className="text-white">Lawful Use:</strong> You will not
              use the Platform for any unlawful, fraudulent, or unauthorized
              purpose.
            </li>
            <li>
              <strong className="text-white">Compliance with Laws:</strong> You
              agree that your use of the Platform will not infringe upon or
              violate any applicable laws, regulations, or third-party rights.
            </li>
          </ul>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            7. User Registration Process
          </h2>
          <p>
            <strong className="text-white">Registration Requirements:</strong>{' '}
            To access certain features and functionalities of the Platform, you
            may be required to register an account. By registering, you agree to
            maintain the confidentiality of your account credentials, including
            your username and password, and to be fully responsible for all
            activities and transactions that occur under your account.
          </p>
          <p className="mt-4">
            <strong className="text-white">Username Policy:</strong> We reserve
            the right to review and approve or disapprove of any username you
            select.
          </p>
          <p className="mt-4">
            <strong className="text-white">Account Security:</strong> You
            acknowledge that you are responsible for safeguarding your account
            credentials and for all activities that occur under your account.
            You agree to notify us immediately of any unauthorized use of your
            account or any other breach of security.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            8. Intellectual Property and Copyright
          </h2>
          <p>
            We respect intellectual property rights and will take appropriate
            actions, including removing or disabling access to infringing
            content and terminating accounts associated with such violations.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            9. Privacy and Data Protection
          </h2>
          <p>
            <strong className="text-white">Commitment to Privacy:</strong> We
            are committed to safeguarding the privacy of our users. For detailed
            information, please review our Privacy Policy.
          </p>
          <p className="mt-4">
            <strong className="text-white">
              Collection of Personal Information:
            </strong>{' '}
            We do not actively collect personal information such as age, gender,
            or address unless such details are voluntarily provided by you.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            10. Subscription and Billing
          </h2>
          <p>
            <strong className="text-white">
              Subscription Plans and Fees:
            </strong>
          </p>
          <ul className="my-4 list-disc space-y-2 ps-6">
            <li>
              <strong className="text-white">Plan Selection:</strong> To access
              various features and services, you may need to select and pay for
              a specific subscription plan.
            </li>
            <li>
              <strong className="text-white">Pricing:</strong> We may offer
              tiered subscription plans based on factors such as usage limits
              and features.
            </li>
            <li>
              <strong className="text-white">Changes to Plans:</strong> We
              reserve the right to modify subscription tiers, pricing, and
              features at any time.
            </li>
          </ul>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            11. Subscription Changes
          </h2>
          <p>
            <strong className="text-white">Upgrading Your Plan:</strong> You can
            upgrade your subscription plan directly through the platform at any
            time. When you upgrade, we&apos;ll issue a pro-rata invoice for the
            new plan based on the remaining days in your current billing cycle.
          </p>
          <p className="mt-4">
            <strong className="text-white">Downgrading Your Plan:</strong> If
            you want to downgrade your subscription, you can do so through the
            platform. Downgrades will only take effect at the end of your
            current billing period.
          </p>
          <p className="mt-4">
            <strong className="text-white">Immediate Changes:</strong> For
            urgent requests, please email us at privacy@askai.sa, and we&apos;ll
            assist you as quickly as possible.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            12. Subscription Cancellation
          </h2>
          <p>
            <strong className="text-white">
              Cancelling Your Subscription:
            </strong>{' '}
            To cancel your subscription, contact us directly at privacy@askai.sa.
            We process cancellations manually to ensure everything is handled
            securely.
          </p>
          <p className="mt-4">
            <strong className="text-white">Account Cancellation:</strong> If you
            wish to cancel your account, please reach out to us at the same
            email address. We&apos;ll process your request promptly.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            13. Refund Policy
          </h2>
          <p>
            <strong className="text-white">No Refunds for Unused Days:</strong>{' '}
            We do not offer refunds for any unused days if you cancel your
            subscription.
          </p>
          <p className="mt-4">
            <strong className="text-white">
              No Refunds for Early Downgrades:
            </strong>{' '}
            If you request a downgrade before the end of your current billing
            term, we will not provide a refund for the remaining days.
          </p>
          <p className="mt-4">
            <strong className="text-white">Contact Us:</strong> If you have any
            questions about our refund policy, feel free to write to us at
            privacy@askai.sa.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            14. Payment Processing and Billing
          </h2>
          <p>
            <strong className="text-white">Payment Processor:</strong> We use a
            third-party payment processor that complies with industry standards
            to handle all billing and payment transactions.
          </p>
          <p className="mt-4">
            <strong className="text-white">Automatic Charges:</strong> The
            payment method you provide will be automatically charged for any
            changes to your services or upon renewal of your subscription.
          </p>
          <p className="mt-4">
            <strong className="text-white">Payment Responsibility:</strong> If
            we are unable to charge your payment method, you are responsible for
            ensuring payment is made by alternative means. Failure to complete
            payment may result in suspension of services.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            15. Fee Revisions
          </h2>
          <p>
            <strong className="text-white">Right to Revise Fees:</strong> We
            reserve the right to change the service fees for any paid plans or
            functionalities at our discretion.
          </p>
          <p className="mt-4">
            <strong className="text-white">Effective Date of Changes:</strong>{' '}
            Any revised fees will apply from the next renewal date of your
            existing subscription plan.
          </p>
          <p className="mt-4">
            <strong className="text-white">Notification of Changes:</strong> We
            will notify you by email if there are any changes to the fees.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            16. Disclaimer of Warranty
          </h2>
          <p>
            <strong className="text-white">Service Availability:</strong> Ask AI
            and its partners do not guarantee that the services or products
            offered will be free from issues or errors. We do not endorse any
            third-party websites or services linked through our platform.
          </p>
          <p className="mt-4">
            <strong className="text-white">Service As-Is:</strong> Our services
            and content are provided on an &quot;as-is&quot; basis. We make no
            promises about their quality, reliability, or that they will be
            error-free or uninterrupted.
          </p>
          <p className="mt-4">
            <strong className="text-white">Internet Use:</strong> Our services
            depend on the internet and your connection. We cannot guarantee
            uninterrupted access or availability.
          </p>
          <p className="mt-4">
            <strong className="text-white">Technical Support:</strong> We offer
            technical support via email at privacy@askai.sa. You are responsible
            for obtaining and maintaining the necessary hardware and internet
            services to use our platform.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            17. Liability Limits
          </h2>
          <p>
            <strong className="text-white">Damage Limits:</strong> To the
            fullest extent permitted by law, we are not liable for indirect or
            consequential damages. Our liability is limited to the amount you
            paid us in the last 12 months or SAR 100, whichever is greater.
          </p>
          <p className="mt-4">
            <strong className="text-white">Exclusions:</strong> We do not cover
            damages related to lost profits or system malfunctions. Some
            jurisdictions may not allow these limitations.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            18. Indemnity
          </h2>
          <p>
            <strong className="text-white">Your Responsibility:</strong> You
            agree to indemnify us against any claims or losses related to your
            use of our services or violations of our terms.
          </p>
          <p className="mt-4">
            <strong className="text-white">Claim Notice:</strong> We will try to
            notify you of any claims, but your indemnification obligations remain
            even if we do not.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            19. Ownership and Intellectual Property
          </h2>
          <p>
            <strong className="text-white">Content Protection:</strong> All
            content on our services, including text, graphics, and images, is
            protected by intellectual property laws. You cannot use or reproduce
            this content without permission.
          </p>
          <p className="mt-4">
            <strong className="text-white">User Submissions:</strong> Any
            content you provide through our services is your own. We may use,
            modify, and display your submissions as needed to operate our
            services.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            20. Terms and Termination
          </h2>
          <p>
            <strong className="text-white">Effective Date:</strong> These terms
            apply from the moment you start using our services. They continue
            until terminated by either party.
          </p>
          <p className="mt-4">
            <strong className="text-white">Termination by You:</strong> You can
            stop using our services anytime by emailing privacy@askai.sa.
          </p>
          <p className="mt-4">
            <strong className="text-white">Termination by Us:</strong> We may
            terminate your access if you violate our terms or for any other
            reason. Termination may result in the loss of your content.
          </p>
          <p className="mt-4">
            <strong className="text-white">Refunds on Termination:</strong> No
            refunds are given for unused days or services after termination.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            21. General Terms
          </h2>
          <p>
            <strong className="text-white">Governing Law:</strong> These terms
            are governed by the laws of the Kingdom of Saudi Arabia. Disputes
            will be resolved in Riyadh, Saudi Arabia.
          </p>
          <p className="mt-4">
            <strong className="text-white">Force Majeure:</strong> We are not
            liable for failure to perform due to unforeseen circumstances beyond
            our control.
          </p>
          <p className="mt-4">
            <strong className="text-white">Notice:</strong> Upon termination,
            you must stop using our services. We may delete your account and
            data, with no refunds provided. Notices related to these Terms will
            be sent to the email addresses provided by you. Notices from us will
            be sent to privacy@askai.sa.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            22. Amendments
          </h2>
          <p>
            We may amend these Terms at any time to improve our platform. Any
            changes will be communicated to you via email or posted on our
            website. Continued use of our Services after such amendments will
            constitute your acceptance of the new Terms. If you do not agree
            with the changes, you must stop using our Services.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            23. Acceptance
          </h2>
          <p>
            By using Ask AI, you acknowledge that you have read, understood, and
            agree to be bound by these Terms. If you do not agree with these
            Terms, you should not use our Services.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            Data Processing Agreement
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            Authentication
          </h3>
          <p>
            Customer data is stored in multi-tenant systems accessible via our
            platform through user interfaces or APIs. We use robust
            authentication mechanisms, including a defined password policy, to
            control access.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            Access Controls and Monitoring
          </h3>
          <p>
            We implement network access control mechanisms to block unauthorized
            protocols and keep our platform secure.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            Data Transmission
          </h3>
          <p>
            All communications on our platform use secured HTTPS protocols. We
            adhere to industry-standard practices for HTTPS to ensure secure
            data transmission.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            Contact Information
          </h3>
          <p>
            For any inquiries related to this Data Processing Agreement, please
            contact us at:
          </p>
          <p className="mt-4 font-medium text-white">Company Name: Ask AI</p>
          <p>Address: Al Wourud, Riyadh, Saudi Arabia</p>
          <p className="font-medium text-white">Email: privacy@askai.sa</p>
          <p>Phone: +966 11 205 3030</p>
        </div>
      </div>
    </main>
  );
}

function ArabicTermsBody() {
  return (
    <main className="relative z-10 px-8 py-16">
      <div dir="rtl" className="mx-auto max-w-4xl text-start">
        <h1 className="mb-4 text-4xl font-light tracking-tight">
          شروط الاستخدام
        </h1>
        <p className="mb-12 font-mono text-sm text-gray-500">
          آخر تحديث: 16 أبريل 2025
        </p>

        <div className="space-y-6 leading-relaxed text-gray-400">
          <p>
            تشكّل شروط الاستخدام هذه (&quot;الشروط&quot;) اتفاقية ملزمة قانونًا
            بينك وبين Ask AI (&quot;نحن&quot;، &quot;لنا&quot;، &quot;الخاصة
            بنا&quot;). بوصولك إلى منصة Ask AI (المشار إليها فيما يلي
            بـ&quot;المنصة&quot;) أو استخدامك لها، فإنك توافق على الالتزام بهذه
            الشروط. وإذا كنت لا توافق على أي جزء من هذه الشروط، فيجب عليك الامتناع
            عن استخدام المنصة والخدمات ذات الصلة أو الوصول إليها.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            1. المقدمة
          </h2>
          <p>
            مرحبًا بك في Ask AI، وهي منصة برمجية للشركات والأفراد تتيح تفاعلات مع
            العملاء مدعومة بالذكاء الاصطناعي، وإدارة الحجوزات، وأتمتة الأعمال.
            تتيح لك منصتنا إنشاء حلول ذكية وإدارتها وتحليلها بكفاءة. يُرجى قراءة
            هذه الشروط بعناية لأنها تحكم استخدامك لموقعنا الإلكتروني وخدماتنا
            ومنصتنا. وإذا كانت لديك أي أسئلة أو استفسارات بخصوص هذه الشروط أو
            خدماتنا، فيُرجى التواصل معنا عبر privacy@askai.sa.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            2. الوصول إلى المنصة
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            2.1 الأهلية
          </h3>
          <p>
            نحن لا نجمع عن قصد معلومات شخصية من الأطفال دون سن 13 عامًا ولا نطلبها
            منهم. إذا كان عمرك أقل من 13 عامًا، فيُرجى عدم محاولة التسجيل في
            خدماتنا أو تزويدنا بأي معلومات شخصية.
          </p>
          <p>
            إذا علمنا أننا قد جمعنا معلومات شخصية من طفل دون سن 13 عامًا، فسنحذف
            تلك المعلومات على الفور.
          </p>
          <p>
            إذا كنت تعتقد أننا ربما جمعنا معلومات شخصية من طفل دون سن 13 عامًا،
            فيُرجى إخطارنا عبر privacy@askai.sa.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            2.2 التسجيل وأمان الحساب
          </h3>
          <p>
            لاستخدام ميزات معينة من المنصة، يجب عليك التسجيل وإنشاء حساب يتضمن اسم
            مستخدم وكلمة مرور.
          </p>
          <p>
            حسابك مخصص لاستخدامك الشخصي فقط ولا ينبغي مشاركته. وقد يؤدي أي انتهاك
            لهذه السياسة إلى إنهاء الحسابات المتأثرة أو جميع الحسابات المرتبطة بك.
          </p>
          <p>
            إذا اشتبهت في أن بيانات اعتماد حسابك قد تعرّضت للاختراق، فيُرجى
            التواصل معنا على الفور. ويُرجى ملاحظة أننا لن نطلب منك أبدًا تأكيد اسم
            المستخدم أو كلمة المرور أو أي تفاصيل شخصية أخرى عبر مراسلات غير
            مطلوبة.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            3. معلومات التواصل
          </h2>
          <p className="font-semibold text-white">Ask AI</p>
          <p>الورود، الرياض، المملكة العربية السعودية</p>
          <p>البريد الإلكتروني: privacy@askai.sa</p>
          <p>الهاتف: +966 11 205 3030</p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            4. استخدام المنصة
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            4.1 الخدمات
          </h3>
          <p>
            نوفّر لك واجهة المنصة ومجموعة من الخدمات (&quot;الخدمات&quot;) مصمّمة
            لتمكينك من الاشتراك في منصتنا واستخدامها للتفاعلات مع العملاء المدعومة
            بالذكاء الاصطناعي، وإدارة الحجوزات، وإنشاء روبوتات المحادثة، وأتمتة
            الأعمال. تُقدَّم هذه الخدمات بحالتها القائمة وقد تساعد في تحسين عمليات
            أعمالك.
          </p>
          <p>
            عند التسجيل، سيُمنح لك حق الوصول إلى المنصة ووظائفها المرتبطة بها وفقًا
            للشروط الموضّحة في هذه الوثيقة.
          </p>
          <p>
            يشير استمرارك في استخدام الخدمات إلى قبولك لهذه الشروط، التي تتضمن
            جميع البنود الواردة في هذه الوثيقة بالإضافة إلى سياسة الخصوصية الخاصة
            بنا.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            5. قيود الخدمة
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.1 المسؤولية عن البيانات
          </h3>
          <p>
            تُقِرّ وتوافق على أنك المسؤول الوحيد عن تقييم سلامة أي بيانات تُقدَّم
            إلينا أو جودتها أو دقتها أو موثوقيتها. وأي قرارات تُتّخذ بناءً على هذه
            البيانات وأي نتائج مترتبة عليها تقع على عاتقك وحدك.
          </p>
          <p>
            تُقِرّ بأننا لا نتحمّل أي مسؤولية عن أي بيانات تتعامل معها أو تُنشئها
            على المنصة.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.2 تعديلات الخدمة
          </h3>
          <p>
            نعمل باستمرار على تحسين منصتنا وقد نُدخل تعديلات أو مراجعات على وظائفها
            ومحتواها وميزاتها ووحداتها، أو نوقف جوانب معينة من الخدمات، وفق
            تقديرنا ودون إشعار مسبق.
          </p>
          <p>
            نحتفظ بالحق في إزالة أي محتوى من الخدمات في أي وقت ولأي سبب، بما في
            ذلك على سبيل المثال لا الحصر في حال وجود ادّعاءات بأن المحتوى ينتهك
            هذه الشروط. وستكون هذه الإزالة وفق تقديرنا المطلق ودون إشعار مسبق.
            وفيما يخص أي اضطرابات أو انقطاعات في الخدمة، يُرجى التواصل معنا عبر
            privacy@askai.sa للحصول على الدعم والتوضيح.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.3 التعليق المؤقت للخدمات
          </h3>
          <p>
            يجوز لنا تقييد الخدمات أو تعليقها مؤقتًا وفق تقديرنا، بما في ذلك لأغراض
            الصيانة أو ترقيات المنصة.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            5.4 الاستخدام غير المقبول للخدمات وسلوك المستخدم
          </h3>
          <p>
            تُقِرّ وتضمن وتوافق على أنك لن تُنشئ أو تساهم بأي محتوى أو مشاركات
            مستخدم، أو تستخدم الخدمات بطريقة:
          </p>
          <ul className="my-4 list-disc space-y-2 ps-6">
            <li>
              تتعدّى على حقوق الملكية الفكرية أو أي حقوق أخرى لأي طرف ثالث (بما في
              ذلك Ask AI) أو تنتهكها
            </li>
            <li>
              تنتهك أي قوانين أو لوائح، بما في ذلك قوانين الرقابة على التصدير
              المعمول بها
            </li>
            <li>
              تكون ضارة أو احتيالية أو مضللة أو تهديدية أو مُضايِقة أو تشهيرية أو
              فاحشة أو غير لائقة بأي شكل آخر
            </li>
            <li>تعرّض أمان حسابك في Ask AI أو حسابات الآخرين للخطر</li>
            <li>
              تحاول الحصول على كلمات المرور أو الحسابات أو غيرها من المعلومات
              الأمنية من مستخدمين آخرين
            </li>
            <li>
              تنتهك أمان أي شبكة حاسوبية، أو تحاول كسر كلمات المرور أو أكواد
              التشفير الأمني
            </li>
            <li>
              تُشغّل أي أنظمة أو عمليات آلية تتداخل مع الأداء السليم للخدمات
            </li>
            <li>
              تَزحف على أي صفحة أو بيانات أو جزء من الخدمات أو المحتوى أو تكشطه أو
              تفهرسه باستخدام وسائل يدوية أو آلية
            </li>
            <li>
              تفكّ تجميع الخدمات أو تجري هندسة عكسية لها أو تحاول الحصول على شفرتها
              المصدرية أو الأفكار الكامنة وراءها
            </li>
          </ul>
          <p>
            يُعدّ أي انتهاك لأي ممّا سبق سببًا لإنهاء حقك في استخدام الخدمات أو
            الوصول إليها، بإشعار أو دون إشعار.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            6. إقرارات المستخدم وضماناته
          </h2>
          <p>بوصولك إلى المنصة واستخدامها، فإنك تُقِرّ وتضمن ما يلي:</p>
          <ul className="my-4 list-disc space-y-2 ps-6">
            <li>
              <strong className="text-white">دقة المعلومات:</strong> تؤكّد أن
              جميع المعلومات المقدَّمة أثناء عملية التسجيل صحيحة ودقيقة وحديثة
              وكاملة على حدّ علمك.
            </li>
            <li>
              <strong className="text-white">الأهلية القانونية:</strong> تُقِرّ
              بأنك تمتلك الأهلية القانونية والصلاحية للدخول في هذه الشروط والأحكام
              والالتزام بجميع الالتزامات والمسؤوليات المنصوص عليها فيها.
            </li>
            <li>
              <strong className="text-white">متطلبات العمر:</strong> تؤكّد أن
              عمرك لا يقل عن 13 عامًا. وإذا كان عمرك أقل من 18 عامًا، فإنك تُقِرّ
              بأنك حصلت على موافقة أحد الوالدين أو الوصي لاستخدام المنصة.
            </li>
            <li>
              <strong className="text-white">عدم الوصول الآلي:</strong> توافق على
              عدم الوصول إلى المنصة باستخدام أي أنظمة آلية، بما في ذلك على سبيل
              المثال لا الحصر الروبوتات أو البرامج النصية أو غيرها من الوسائل
              الآلية التي تتجاوز واجهة المستخدم العادية.
            </li>
            <li>
              <strong className="text-white">الاستخدام المشروع:</strong> لن
              تستخدم المنصة لأي غرض غير قانوني أو احتيالي أو غير مصرّح به.
            </li>
            <li>
              <strong className="text-white">الامتثال للقوانين:</strong> توافق
              على أن استخدامك للمنصة لن يتعدّى على أي قوانين أو لوائح أو حقوق
              أطراف ثالثة معمول بها أو ينتهكها.
            </li>
          </ul>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            7. عملية تسجيل المستخدم
          </h2>
          <p>
            <strong className="text-white">متطلبات التسجيل:</strong> للوصول إلى
            ميزات ووظائف معينة من المنصة، قد يُطلب منك تسجيل حساب. وبالتسجيل، فإنك
            توافق على الحفاظ على سرية بيانات اعتماد حسابك، بما في ذلك اسم المستخدم
            وكلمة المرور، وعلى تحمّل المسؤولية الكاملة عن جميع الأنشطة والمعاملات
            التي تتم ضمن حسابك.
          </p>
          <p className="mt-4">
            <strong className="text-white">سياسة اسم المستخدم:</strong> نحتفظ
            بالحق في مراجعة أي اسم مستخدم تختاره والموافقة عليه أو رفضه.
          </p>
          <p className="mt-4">
            <strong className="text-white">أمان الحساب:</strong> تُقِرّ بأنك
            مسؤول عن حماية بيانات اعتماد حسابك وعن جميع الأنشطة التي تتم ضمن
            حسابك. وتوافق على إخطارنا على الفور بأي استخدام غير مصرّح به لحسابك أو
            أي خرق أمني آخر.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            8. الملكية الفكرية وحقوق النشر
          </h2>
          <p>
            نحترم حقوق الملكية الفكرية وسنتّخذ الإجراءات المناسبة، بما في ذلك
            إزالة المحتوى المخالف أو تعطيل الوصول إليه وإنهاء الحسابات المرتبطة
            بهذه الانتهاكات.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            9. الخصوصية وحماية البيانات
          </h2>
          <p>
            <strong className="text-white">الالتزام بالخصوصية:</strong> نلتزم
            بحماية خصوصية مستخدمينا. وللحصول على معلومات مفصّلة، يُرجى مراجعة
            سياسة الخصوصية الخاصة بنا.
          </p>
          <p className="mt-4">
            <strong className="text-white">جمع المعلومات الشخصية:</strong> لا
            نجمع بنشاط معلومات شخصية مثل العمر أو الجنس أو العنوان ما لم تُقدَّم
            هذه التفاصيل طوعًا من قِبلك.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            10. الاشتراك والفوترة
          </h2>
          <p>
            <strong className="text-white">خطط الاشتراك والرسوم:</strong>
          </p>
          <ul className="my-4 list-disc space-y-2 ps-6">
            <li>
              <strong className="text-white">اختيار الخطة:</strong> للوصول إلى
              ميزات وخدمات مختلفة، قد تحتاج إلى اختيار خطة اشتراك معينة ودفع
              رسومها.
            </li>
            <li>
              <strong className="text-white">التسعير:</strong> قد نقدّم خطط
              اشتراك متدرّجة بناءً على عوامل مثل حدود الاستخدام والميزات.
            </li>
            <li>
              <strong className="text-white">التغييرات على الخطط:</strong> نحتفظ
              بالحق في تعديل مستويات الاشتراك والأسعار والميزات في أي وقت.
            </li>
          </ul>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            11. تغييرات الاشتراك
          </h2>
          <p>
            <strong className="text-white">ترقية خطتك:</strong> يمكنك ترقية خطة
            اشتراكك مباشرةً عبر المنصة في أي وقت. وعند الترقية، سنُصدر فاتورة
            تناسبية للخطة الجديدة بناءً على الأيام المتبقية في دورة الفوترة
            الحالية.
          </p>
          <p className="mt-4">
            <strong className="text-white">خفض خطتك:</strong> إذا أردت خفض
            اشتراكك، فيمكنك القيام بذلك عبر المنصة. ولن يدخل الخفض حيّز التنفيذ إلا
            في نهاية فترة الفوترة الحالية.
          </p>
          <p className="mt-4">
            <strong className="text-white">التغييرات الفورية:</strong> للطلبات
            العاجلة، يُرجى مراسلتنا عبر البريد الإلكتروني privacy@askai.sa،
            وسنساعدك بأسرع وقت ممكن.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            12. إلغاء الاشتراك
          </h2>
          <p>
            <strong className="text-white">إلغاء اشتراكك:</strong> لإلغاء
            اشتراكك، تواصل معنا مباشرةً عبر privacy@askai.sa. نحن نعالج عمليات
            الإلغاء يدويًا لضمان التعامل مع كل شيء بأمان.
          </p>
          <p className="mt-4">
            <strong className="text-white">إلغاء الحساب:</strong> إذا كنت ترغب في
            إلغاء حسابك، فيُرجى التواصل معنا على عنوان البريد الإلكتروني نفسه.
            وسنعالج طلبك على الفور.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            13. سياسة الاسترداد
          </h2>
          <p>
            <strong className="text-white">
              لا استرداد عن الأيام غير المستخدَمة:
            </strong>{' '}
            نحن لا نقدّم أي استرداد عن أي أيام غير مستخدَمة في حال إلغائك
            لاشتراكك.
          </p>
          <p className="mt-4">
            <strong className="text-white">
              لا استرداد عن عمليات الخفض المبكرة:
            </strong>{' '}
            إذا طلبت خفض الاشتراك قبل نهاية فترة الفوترة الحالية، فلن نقدّم
            استردادًا عن الأيام المتبقية.
          </p>
          <p className="mt-4">
            <strong className="text-white">تواصل معنا:</strong> إذا كانت لديك أي
            أسئلة بخصوص سياسة الاسترداد لدينا، فلا تتردد في مراسلتنا عبر
            privacy@askai.sa.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            14. معالجة الدفع والفوترة
          </h2>
          <p>
            <strong className="text-white">معالج الدفع:</strong> نستخدم معالج دفع
            تابعًا لطرف ثالث يتوافق مع معايير الصناعة للتعامل مع جميع معاملات
            الفوترة والدفع.
          </p>
          <p className="mt-4">
            <strong className="text-white">الرسوم التلقائية:</strong> سيُحصَّل
            تلقائيًا من طريقة الدفع التي تقدّمها مقابل أي تغييرات على خدماتك أو عند
            تجديد اشتراكك.
          </p>
          <p className="mt-4">
            <strong className="text-white">مسؤولية الدفع:</strong> إذا تعذّر
            علينا تحصيل المبلغ من طريقة الدفع الخاصة بك، فأنت مسؤول عن ضمان سداد
            الدفعة بوسائل بديلة. وقد يؤدي عدم إتمام الدفع إلى تعليق الخدمات.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            15. تعديلات الرسوم
          </h2>
          <p>
            <strong className="text-white">الحق في تعديل الرسوم:</strong> نحتفظ
            بالحق في تغيير رسوم الخدمة لأي خطط مدفوعة أو وظائف وفق تقديرنا.
          </p>
          <p className="mt-4">
            <strong className="text-white">تاريخ سريان التغييرات:</strong> تسري
            أي رسوم معدَّلة اعتبارًا من تاريخ التجديد التالي لخطة اشتراكك الحالية.
          </p>
          <p className="mt-4">
            <strong className="text-white">الإخطار بالتغييرات:</strong> سنُخطرك
            عبر البريد الإلكتروني في حال وجود أي تغييرات على الرسوم.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            16. إخلاء المسؤولية عن الضمان
          </h2>
          <p>
            <strong className="text-white">توفّر الخدمة:</strong> لا تضمن Ask AI
            وشركاؤها أن تكون الخدمات أو المنتجات المقدَّمة خالية من المشكلات أو
            الأخطاء. ونحن لا نُقِرّ أي مواقع إلكترونية أو خدمات تابعة لأطراف ثالثة
            مرتبطة عبر منصتنا.
          </p>
          <p className="mt-4">
            <strong className="text-white">الخدمة كما هي:</strong> تُقدَّم
            خدماتنا ومحتوانا على أساس &quot;كما هي&quot;. ولا نقدّم أي وعود بشأن
            جودتها أو موثوقيتها أو خلوّها من الأخطاء أو عدم انقطاعها.
          </p>
          <p className="mt-4">
            <strong className="text-white">استخدام الإنترنت:</strong> تعتمد
            خدماتنا على الإنترنت وعلى اتصالك. ولا يمكننا ضمان وصول أو توفّر دون
            انقطاع.
          </p>
          <p className="mt-4">
            <strong className="text-white">الدعم الفني:</strong> نقدّم الدعم
            الفني عبر البريد الإلكتروني privacy@askai.sa. وأنت مسؤول عن الحصول على
            الأجهزة وخدمات الإنترنت اللازمة لاستخدام منصتنا والمحافظة عليها.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            17. حدود المسؤولية
          </h2>
          <p>
            <strong className="text-white">حدود الأضرار:</strong> إلى أقصى حدّ
            يسمح به القانون، نحن غير مسؤولين عن الأضرار غير المباشرة أو التبعية.
            وتقتصر مسؤوليتنا على المبلغ الذي دفعته لنا خلال الاثني عشر شهرًا
            الأخيرة أو 100 ريال سعودي (SAR 100)، أيهما أكبر.
          </p>
          <p className="mt-4">
            <strong className="text-white">الاستثناءات:</strong> نحن لا نغطّي
            الأضرار المتعلّقة بالأرباح المفقودة أو أعطال النظام. وقد لا تسمح بعض
            الولايات القضائية بهذه القيود.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            18. التعويض
          </h2>
          <p>
            <strong className="text-white">مسؤوليتك:</strong> توافق على تعويضنا
            عن أي مطالبات أو خسائر تتعلّق باستخدامك لخدماتنا أو بانتهاكاتك
            لشروطنا.
          </p>
          <p className="mt-4">
            <strong className="text-white">إشعار المطالبة:</strong> سنحاول إخطارك
            بأي مطالبات، لكن تظل التزامات التعويض الخاصة بك قائمة حتى إن لم نفعل
            ذلك.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            19. الملكية والملكية الفكرية
          </h2>
          <p>
            <strong className="text-white">حماية المحتوى:</strong> جميع المحتوى
            الموجود على خدماتنا، بما في ذلك النصوص والرسومات والصور، محمي بموجب
            قوانين الملكية الفكرية. ولا يمكنك استخدام هذا المحتوى أو إعادة إنتاجه
            دون إذن.
          </p>
          <p className="mt-4">
            <strong className="text-white">مشاركات المستخدم:</strong> أي محتوى
            تقدّمه عبر خدماتنا هو ملكك. وقد نستخدم مشاركاتك ونعدّلها ونعرضها حسب
            الحاجة لتشغيل خدماتنا.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            20. المدة والإنهاء
          </h2>
          <p>
            <strong className="text-white">تاريخ السريان:</strong> تسري هذه
            الشروط منذ لحظة بدء استخدامك لخدماتنا. وتستمر إلى أن يُنهيها أيّ من
            الطرفين.
          </p>
          <p className="mt-4">
            <strong className="text-white">الإنهاء من جانبك:</strong> يمكنك
            التوقّف عن استخدام خدماتنا في أي وقت بمراسلتنا عبر privacy@askai.sa.
          </p>
          <p className="mt-4">
            <strong className="text-white">الإنهاء من جانبنا:</strong> يجوز لنا
            إنهاء وصولك إذا انتهكت شروطنا أو لأي سبب آخر. وقد يؤدي الإنهاء إلى
            فقدان محتواك.
          </p>
          <p className="mt-4">
            <strong className="text-white">الاسترداد عند الإنهاء:</strong> لا
            يُمنح أي استرداد عن الأيام أو الخدمات غير المستخدَمة بعد الإنهاء.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            21. الأحكام العامة
          </h2>
          <p>
            <strong className="text-white">القانون الحاكم:</strong> تخضع هذه
            الشروط لقوانين المملكة العربية السعودية. وتُحلّ النزاعات في الرياض،
            المملكة العربية السعودية.
          </p>
          <p className="mt-4">
            <strong className="text-white">القوة القاهرة:</strong> نحن غير
            مسؤولين عن الإخفاق في الأداء بسبب ظروف غير متوقّعة خارجة عن سيطرتنا.
          </p>
          <p className="mt-4">
            <strong className="text-white">الإشعار:</strong> عند الإنهاء، يجب
            عليك التوقّف عن استخدام خدماتنا. وقد نحذف حسابك وبياناتك دون تقديم أي
            استرداد. وتُرسَل الإشعارات المتعلّقة بهذه الشروط إلى عناوين البريد
            الإلكتروني التي تقدّمها. وتُرسَل الإشعارات منّا إلى privacy@askai.sa.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            22. التعديلات
          </h2>
          <p>
            يجوز لنا تعديل هذه الشروط في أي وقت لتحسين منصتنا. وسيتم إبلاغك بأي
            تغييرات عبر البريد الإلكتروني أو نشرها على موقعنا الإلكتروني. ويُشكّل
            استمرارك في استخدام خدماتنا بعد هذه التعديلات قبولًا منك للشروط
            الجديدة. وإذا كنت لا توافق على التغييرات، فيجب عليك التوقّف عن استخدام
            خدماتنا.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            23. القبول
          </h2>
          <p>
            باستخدامك Ask AI، فإنك تُقِرّ بأنك قرأت هذه الشروط وفهمتها وتوافق على
            الالتزام بها. وإذا كنت لا توافق على هذه الشروط، فلا ينبغي لك استخدام
            خدماتنا.
          </p>

          <h2 className="mb-4 mt-12 text-2xl font-semibold text-white">
            اتفاقية معالجة البيانات
          </h2>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            المصادقة
          </h3>
          <p>
            تُخزَّن بيانات العملاء في أنظمة متعددة المستأجرين يمكن الوصول إليها
            عبر منصتنا من خلال واجهات المستخدم أو واجهات برمجة التطبيقات (APIs).
            ونستخدم آليات مصادقة قوية، بما في ذلك سياسة محدَّدة لكلمات المرور،
            للتحكم في الوصول.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            ضوابط الوصول والمراقبة
          </h3>
          <p>
            ننفّذ آليات للتحكم في الوصول إلى الشبكة لحظر البروتوكولات غير المصرّح
            بها والحفاظ على أمان منصتنا.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            نقل البيانات
          </h3>
          <p>
            تستخدم جميع الاتصالات على منصتنا بروتوكولات HTTPS الآمنة. ونلتزم
            بالممارسات المتوافقة مع معايير الصناعة لبروتوكول HTTPS لضمان النقل
            الآمن للبيانات.
          </p>

          <h3 className="mb-3 mt-8 text-xl font-semibold text-gray-200">
            معلومات التواصل
          </h3>
          <p>
            لأي استفسارات تتعلّق باتفاقية معالجة البيانات هذه، يُرجى التواصل معنا
            عبر:
          </p>
          <p className="mt-4 font-medium text-white">اسم الشركة: Ask AI</p>
          <p>العنوان: الورود، الرياض، المملكة العربية السعودية</p>
          <p className="font-medium text-white">
            البريد الإلكتروني: privacy@askai.sa
          </p>
          <p>الهاتف: +966 11 205 3030</p>
        </div>
      </div>
    </main>
  );
}
